import ActionMenu from "Components/Common/ActionMenu";
import FilterUser, { UserFilterValues } from "Components/Common/FilterUser";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import Icon from "Components/Icons/Icon";
import { GlobalImage } from "config/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import {
  adminDeleteUser,
  adminUsersList,
  initiateImpersonate,
  manageDisableUser,
} from "slices/thunks";
import { capitalizeText } from "utils/formatText";
import { ErrorHandler } from "utils/toast";

const Users = () => {
  const [showItemFilter, setShowItemFilter] = useState<boolean>(false);
  const navigate = useNavigate();
  const { isLoading, usersList, meta } = useAppSelector((state) => state.admin);
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filters, setFilters] = useState<UserFilterValues | null>(null);
  const [isImpersonateLoading, setIsImpersonateLoading] =
    useState<boolean>(false);

  useEffect(() => {
    dispatch(
      adminUsersList({
        search: searchText,
        page: currentPage,
        status: filters?.status,
        subscription: filters?.subscription,
        vertical: filters?.vertical as any,
      })
    );
  }, [dispatch, searchText, currentPage, filters]);

  const handleSearchUser = useCallback((searchText: string) => {
    setSearchText(searchText);
    setCurrentPage(1);
  }, []);
  const [isActiveLoading, setIsActiveLoading] = useState<boolean>(false);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleFilterChange = useCallback((f: UserFilterValues) => {
    setFilters(f);
    setCurrentPage(1);
  }, []);

  const handleImpersonate = useCallback(
    async (userId: number) => {
      try {
        setIsImpersonateLoading(true);
        await dispatch(initiateImpersonate(userId))?.unwrap();
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("Error initiating impersonation:", error);
        ErrorHandler(error);
      } finally {
        setIsImpersonateLoading(false);
      }
    },
    [dispatch, navigate]
  );

  const handleToggle = useCallback(
    async (id: number) => {
      try {
        setIsActiveLoading(true);
        await dispatch(manageDisableUser(id));
      } catch (error) {
        console.log("Error toggling user active status:", error);
        ErrorHandler(error);
      } finally {
        setIsActiveLoading(false);
      }
    },
    [dispatch]
  );

  const handleRemoveUser = useCallback(
    (userId: number) => {
      dispatch(adminDeleteUser(userId));
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      {
        header: "USER",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const user = row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <img
                src={user.avatar || GlobalImage?.userPlaceHolder}
                alt={user.name}
                className="user-img rounded-circle"
                style={{ width: 36, height: 36 }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    GlobalImage?.userPlaceHolder || "";
                }}
              />
              <span className="fw-medium text-black2D fs-14">
                {user.name ?? "-"}
              </span>
            </div>
          );
        },
      },
      {
        header: "TRADE",
        accessorKey: "trade",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "COMPANY",
        accessorKey: "company_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "TEAM",
        accessorKey: "team_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "QUOTES",
        accessorKey: "quotes_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "INVOICES",
        accessorKey: "invoices_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "PHONE",
        accessorKey: "phone",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "EMAIL",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "SUBSCRIPTION",
        accessorKey: "subscription_status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {capitalizeText(cell.getValue())}
          </span>
        ),
      },
      {
        header: "IS ACTIVE",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const isActive = cell.getValue();
          const rowId = cell?.row?.original?.id;

          return (
            <span
              className={`toggle-switch ${isActive ? "on" : "off"}`}
              onClick={() => handleToggle(rowId)}
            >
              <span className={`toggle-label ${isActive ? "on" : "off"}`}>
                {isActive ? "Yes" : "No"}
              </span>
              <div className={`toggle-circle ${isActive ? "on" : "off"}`} />
            </span>
          );
        },
      },
      {
        header: "ACTION",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <ActionMenu
              isView
              isDelete
              isImpersonate
              onView={() =>
                navigate("/user-detail", {
                  state: { userDetail: row.original },
                })
              }
              onImpersonate={handleImpersonate.bind(null, row.original.id)}
              onDelete={handleRemoveUser.bind(null, row.original.id)}
            />
          );
        },
      },
    ],
    [handleImpersonate, handleRemoveUser, handleToggle, navigate]
  );

  return (
    <div className="page-content">
      <FullScreenLoader
        isLoading={isImpersonateLoading || isActiveLoading}
        message={
          isImpersonateLoading
            ? "Switching to user account..."
            : "Updating user status..."
        }
      />
      <Container fluid>
        <PageHeaderActions title="Users" subtitle="Manage all your users" />
        <Card id="membersTable" className="br-10 bg-white">
          <CardBody className="pt-0">
            <TableContainer
              columns={columns}
              data={usersList}
              isGlobalFilter={true}
              divClass="table-responsive table-card mb-2 bg-white"
              tableClass="align-middle table-nowrap table-borderless"
              thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
              theadClass="table-light"
              SearchPlaceholder="Search User"
              searchAlign="left"
              rightContent={
                <CustomButton
                  text="Filter"
                  className="bg-blue br-7"
                  leftIcon={<Icon name="Filter" className="ms-1" />}
                  onClick={() => {
                    setShowItemFilter(true);
                  }}
                />
              }
              onSearch={handleSearchUser}
              onPageChange={handlePageChange}
              totalPages={meta?.last_page || 0}
              currentPage={meta?.current_page}
              fromPage={meta?.from}
              toPage={meta?.to}
              totalItems={meta?.total}
              isInitialLoading={
                isLoading && !usersList?.length && !isActiveLoading
              }
              isPaginationLoading={
                isLoading && (usersList?.length ?? 0) > 0 && !isActiveLoading
              }
            />
          </CardBody>
        </Card>
      </Container>

      <FilterUser
        onCloseClick={() => setShowItemFilter(false)}
        show={showItemFilter}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
};

export default Users;
